interface LocalStorageRingBufferType<T> {
  push: (item: T) => void;
  toArray: () => T[];
}

class LocalStorageRingBuffer<T> implements LocalStorageRingBufferType<T> {
  private namespace: string;

  private size: number;

  private buffer: T[];

  private pointer: number;

  private count: number;

  private classId?: string;

  constructor(namespace: string, size: number, useStorage: boolean, classId?: string) {
    this.namespace = namespace;
    this.size = size;
    this.classId = classId; // only required if useStorage is true

    if (useStorage) {
      const oldClassId = localStorage.getItem("class-id");
      if (oldClassId !== this.classId) {
        localStorage.removeItem(this.makeKey("buffer"));
        localStorage.removeItem(this.makeKey("pointer"));
        localStorage.removeItem(this.makeKey("count"));
      }

      this.buffer = this.getBufferFromStorage();
      this.pointer = this.getNumberFromStorage("pointer", 0);
      this.count = this.getNumberFromStorage("count", 0);
    } else {
      this.buffer = new Array<T>(size);
      this.pointer = 0;
      this.count = 0;
    }

    addEventListener("unload", () => {
      if (useStorage) {
        this.storeBuffer();
        this.storeNumber("pointer", this.pointer);
        this.storeNumber("count", this.count);
        localStorage.setItem("class-id", this.classId || "");
      }
    });
  }

  private makeKey(key: string) {
    return `${this.namespace}-${key}`;
  }

  private getNumberFromStorage(key: string, valueIfNotExist: number) {
    const value = localStorage.getItem(this.makeKey(key));
    return value !== null ? Number(value) : valueIfNotExist;
  }

  private storeNumber(key: string, value: number) {
    localStorage.setItem(this.makeKey(key), `${value}`);
  }

  private getBufferFromStorage() {
    const value = localStorage.getItem(this.makeKey("buffer"));
    return value !== null ? (JSON.parse(value) as T[]) : new Array<T>(this.size);
  }

  private storeBuffer() {
    localStorage.setItem(this.makeKey("buffer"), JSON.stringify(this.buffer));
  }

  push(item: T) {
    this.buffer[this.pointer] = item;
    this.pointer = (this.size + this.pointer + 1) % this.size;
    this.count += 1;
  }

  last() {
    return this.buffer[(this.size + this.pointer - 1) % this.size];
  }

  lastN(n: number) {
    const arr = [];
    for (let j = 0; j < n; j += 1) {
      const index = (this.size + this.pointer - j - 1) % this.size;
      if (this.buffer[index] !== undefined && this.buffer[index] !== null) {
        arr.push(this.buffer[index]);
      }
    }
    return arr as T[];
  }

  toArray() {
    const arr = [];
    for (let j = 0; j < this.size; j += 1) {
      const index = (this.size + this.pointer + j) % this.size;
      if (this.buffer[index] !== undefined && this.buffer[index] !== null) {
        arr.push(this.buffer[index]);
      }
    }
    return arr as T[];
  }
}

export { LocalStorageRingBuffer };
